import { MAEKER_CATEGORY_SEARCH_DEFAULT_VALUE as CATEGORY_DEFAULT, CATEGORY_OPTIONS } from '~js/constants'
import _ from 'lodash'
import i18n from '@/i18n'
import buildingApiService from '@/services/api/building'
import propertyApiService from '@/services/api/property'

const SCOPE = {
  building: 'building',
  group: 'group'
}

const state = {
  isSearchInit: false,
  searchData: [],
  searchKeywords: '',
  categoryOptions: CATEGORY_OPTIONS,
  selectedCategory: CATEGORY_OPTIONS[0],
  searchCategory: CATEGORY_DEFAULT,
  searchLiveTourData: [],
  searchCompanyData: []
}

const transformMultilangField = field => {
  if (field) {
    return typeof field === 'string' ? JSON.parse(field) : typeof field === 'object' ? field : {}
  }
  return {}
}

const parseCategoryHandler = rawCategory => {
  const category = (typeof rawCategory === 'string' ? JSON.parse(rawCategory) : rawCategory) || []
  if (Array.isArray(category)) {
    return category.map(tag => {
      if (typeof tag === 'string') {
        return tag.trim()
      } else {
        console.error('Error Tag Type In Category', JSON.stringify(tag))
        return ''
      }
    })
  } else {
    console.error('Error Category Type', category)
    return []
  }
}

const formatMarker = marker => {
  const result = {}
  if (Object.values(marker).length === 0) return result
  result.photo = marker.photo ? marker.photo.split(',')[0] : ''
  result.type = marker.type || ''
  result.name = marker.name || ''
  result.nameMulti = transformMultilangField(marker.nameMulti)
  result.descriptionMulti = transformMultilangField(marker.descriptionMulti)
  result.category = parseCategoryHandler(marker.category)
  result.categoryMulti = transformMultilangField(marker.categoryMulti)
  result.description = marker.description || ''
  result.iconType = marker.iconType || ''
  result.iconUrl = marker.iconUrl || ''
  result.useCustomIcon = marker.useCustomIcon || false
  // markerId in /allMarkers api, objectId in original panorama markers
  result.markerId = marker.markerId || marker.objectId || ''
  result.buildingId = marker.buildingId || ''
  result.panoramaId = marker.panoramaId || ''
  result.panoramaIndex = marker.panoramaIndex > 0 ? marker.panoramaIndex + 1 : 1
  if (marker.type === 'videoplay') {
    result.videoplayAttribute = marker.videoplayAttribute
  }
  if (marker.type === 'multitag') {
    result.multitagInfo = marker.multitagInfo || []
  }
  result.kind = 'product'
  result.raw = marker
  return result
}

const formatCompany = company => {
  const result = {}
  result.name = company.company || ''
  result.nameMulti = transformMultilangField(company.nameMulti)
  result.description = company.description || ''
  result.descriptionMulti = transformMultilangField(company.descriptionMulti)
  result.category = parseCategoryHandler(company.category)
  result.categoryMulti = transformMultilangField(company.categoryMulti)
  result.logoUrl = company.logoUrl || null
  result.email = company.email || ''
  result.privacy = company.privacy || ''
  result.terms = company.terms || ''
  result.kind = 'company'
  result.objectId = company.objectId
  result.raw = company
  return result
}

const formatBuilding = building => {
  const result = _.cloneDeep(building)
  result.nameMulti = transformMultilangField(building.nameMulti)
  result.descriptionMulti = transformMultilangField(building.descriptionMulti)
  result.category = []
  result.kind = 'livetour'
  return result
}

const sortedSearchResultHandler = result => {
  const copyResult = JSON.parse(JSON.stringify(result))
  const sortHandler = (a, b) => {
    const lang = i18n.locale
    const nameA = (a.nameMulti[lang] || a.name).trim()
    const nameB = (b.nameMulti[lang] || b.name).trim()
    if (nameA === '') return 1
    if (nameB === '') return -1
    return nameA.localeCompare(nameB, lang, { ignorePunctuation: true })
  }
  copyResult.searchResult.sort(sortHandler)
  copyResult.searchRelated.sort(sortHandler)
  return copyResult
}

const searchFilterHandler = (data, filterKeys, selectedCategory, searchKeywords) => {
  // 只有 category 符合 searchKeywords 歸類在 searchRelated
  // filterKeys 符合的歸類在 searchResult
  return data.filter(item => {
    if (selectedCategory) {
      return item.kind === selectedCategory
    }
    return true
  }).reduce((result, item) => {
    if (searchKeywords) {
      const keyword = searchKeywords.toLowerCase()
      if (filterKeys.some(key => {
        return (_.property(key)(item) || '').toLowerCase().includes(keyword)
      })) {
        result.searchResult.push(item)
      } else if (item.category.some(category => {
        return category.toLowerCase().includes(keyword)
      })) {
        result.searchRelated.push(item)
      }
    } else {
      result.searchResult.push(item)
    }
    return result
  }, { searchResult: [], searchRelated: [] })
}

export const getters = {
  isSearchInit: state => state.isSearchInit,
  searchData: state => state.searchData,
  searchKeywords: state => state.searchKeywords,
  categoryOptions: state => state.categoryOptions,
  searchCategory: state => state.searchCategory,
  selectedCategory: state => state.selectedCategory,
  searchLiveTourData: state => state.searchLiveTourData,
  searchCompanyData: state => state.searchCompanyData,
  searchLiveTourId: state => state.searchLiveTourData.map(liveTour => liveTour.objectId),
  searchBuildingData: (state, getters, rootState) => {
    return rootState.property.property.buildings ? rootState.property.property.buildings.filter(building => !building.unavailable).map(formatBuilding) : []
  },
  // 所有type為 tag gallery narration memo 的 markers
  // 210531: add multitag support
  searchAbleMarkers: (state, getters) => {
    const searchAbleTypes = ['tag', 'gallery', 'narration', 'memo', 'popup', 'arMakerTag', 'multitag', 'videoplay', 'pdf']
    return getters.searchData
      .filter(marker => searchAbleTypes.includes(marker.type))
      .reduce((result, marker) => {
        if (marker.type === 'multitag') {
          marker.multitagInfo.forEach(tag => {
            result.push({
              ...marker,
              ...tag
            })
          })
        } else {
          result.push(marker)
        }
        return result
      }, [])
      .sort((a, b) => {
        return a.name > b.name ? 1 : -1
      })
  },
  // 根據searchKeywords 過濾後的markers
  searchedMarkers: (state, getters) => {
    // 舊版搜尋邏輯：只在 HQ = TVA 適用
    const lang = i18n.locale
    const filterKeys = [`nameMulti.${lang}`, `descriptionMulti.${lang}`, 'name', 'description']
    if (state.searchKeywords === '') return getters.searchAbleMarkers
    return getters.searchAbleMarkers.filter(marker => {
      return filterKeys.some(key => {
        const keyword = state.searchKeywords.toLowerCase()
        const value = (_.property(key)(marker) || '').toLowerCase()
        return value.includes(keyword)
      })
    })
  },
  // 所有marker的category集合
  searchAbleCategories: (state, getters) => {
    // 舊版搜尋邏輯：只在 HQ = TVA 適用
    const markerCategories = getters.searchAbleMarkers
      .reduce((result, marker) => {
        return result.concat(marker.category || [])
      }, [])
      .sort((a, b) => a > b ? 1 : -1)
    return [...new Set(markerCategories)]
  },
  // 根據searchKeywords 及 searchCategory 過濾後的markers
  searchResult: (state, getters) => {
    // 舊版搜尋邏輯：只在 HQ = TVA 適用
    if (state.searchCategory === CATEGORY_DEFAULT) return getters.searchedMarkers
    if (state.searchLiveTourData.length > 0) return getters.searchedMarkers.filter(marker => getters.searchLiveTourId.includes(marker.buildingId))
    return getters.searchedMarkers.filter(marker => marker.category.includes(state.searchCategory))
  },
  // 根據 searchCategory 過濾後的markers 並不符合 searchKeywords (若符合會出現在searchResult)
  relatedResult: (state, getters) => {
    // 舊版搜尋邏輯：只在 HQ = TVA 適用
    if (state.searchCategory === CATEGORY_DEFAULT || state.searchLiveTourData.length > 0) return []
    return getters.searchAbleMarkers.filter(marker => marker.category.includes(state.searchCategory))
      .filter(marker => !getters.searchResult.includes(marker))
  },
  // 當group markerFinderEnable 為true 搜尋範圍該group
  // 不論是否是group 只要該building markerFinderEnable 為true  搜尋範圍該building
  searchScope: (state, getters) => {
    if (getters.isPropertyActive && getters.property.markerFinderEnable) return SCOPE.group
    if (getters.currentBuilding.markerFinderEnable) return SCOPE.building
    return false
  },
  // 是否開啟 search 功能
  isMarkerFinderEnable: (state, getters) => {
    return !!getters.searchScope
  },
  // 搜尋優化改動：markers 分類為 product
  searchedProducts: (state, getters) => {
    const lang = i18n.locale
    const filterKeys = [`nameMulti.${lang}`, `descriptionMulti.${lang}`, 'name', 'description']
    return searchFilterHandler(getters.searchAbleMarkers, filterKeys, getters.selectedCategory, getters.searchKeywords)
  },
  searchedCompanys: (state, getters) => {
    const filterKeys = ['name', 'description']
    return searchFilterHandler(getters.searchCompanyData, filterKeys, getters.selectedCategory, getters.searchKeywords)
  },
  searchedBuildings: (state, getters) => {
    const lang = i18n.locale
    const filterKeys = [`nameMulti.${lang}`, `descriptionMulti.${lang}`, 'name', 'description']
    return searchFilterHandler(getters.searchBuildingData, filterKeys, getters.selectedCategory, getters.searchKeywords)
  },
  sortedSearchedProducts: (state, getters) => {
    return sortedSearchResultHandler(getters.searchedProducts)
  },
  sortedSearchedCompanys: (state, getters) => {
    return sortedSearchResultHandler(getters.searchedCompanys)
  },
  sortedSearchedBuildings: (state, getters) => {
    return sortedSearchResultHandler(getters.searchedBuildings)
  },
  combineSearchResult: (state, getters) => {
    // return [].concat(...getters.sortedSearchedCompanys.searchResult, ...getters.sortedSearchedProducts.searchResult, ...getters.sortedSearchedBuildings.searchResult)
    return [].concat(getters.sortedSearchedCompanys.searchResult, getters.sortedSearchedProducts.searchResult, getters.sortedSearchedBuildings.searchResult)
  },
  combineRelatedResult: (state, getters) => {
    // return [].concat(...getters.sortedSearchedCompanys.searchRelated, ...getters.sortedSearchedProducts.searchRelated, ...getters.sortedSearchedBuildings.searchRelated)
    return [].concat(getters.sortedSearchedCompanys.searchRelated, getters.sortedSearchedProducts.searchRelated, getters.sortedSearchedBuildings.searchRelated)
  }
}

export const actions = {
  async initSearch ({ commit, getters, dispatch }) {
    if (getters.isSearchInit) {
      return
    }
    let markers = []
    let companys = []
    try {
      if (getters.searchScope === SCOPE.group) {
        const markerData = await propertyApiService.fetchPropertyMarkers(getters.property.objectId)
        markers = markerData != null ? markerData.markers.map(formatMarker) : []
        const companyData = await propertyApiService.fetchPropertyCompanys(getters.property.objectId)
        companys = companyData.companies
          ? Object.keys(companyData.companies).map(key => {
            const data = companyData.companies[key]
            data.objectId = key
            return formatCompany(data)
          })
          : []
      } else {
        markers = getters.buildingMarkers.map(formatMarker)
        companys = Object.values(getters.user.companyInfo).length > 0
          ? [{
              ...getters.user.companyInfo,
              objectId: getters.user.objectId
            }].map(formatCompany)
          : []
      }
    // eslint-disable-next-line no-useless-catch
    } catch (error) {
      throw error
    } finally {
      commit('SET_SEARCH_DATA', markers)
      commit('SET_SEARCH_COMPANY_DATA', companys)
      commit('SET_SEARCH_INIT', true)
    }
  },
  setSearchKeywords ({ commit }, searchKeywords) {
    commit('SET_SEARCH_KEYWORDS', searchKeywords)
  },
  setSearchCategory ({ commit }, searchCategory) {
    commit('SET_SEARCH_CATEGORY', searchCategory)
  },
  setSelectedCategory ({ commit }, category) {
    commit('SET_SELECTED_CATEGORY', category)
  },
  setSearchLiveTourData ({ commit }, searchLiveTourData) {
    commit('SET_SEARCH_LIVETOUR_DATA', searchLiveTourData)
  },
  setSearchCompanyData ({ commit }, searchCompanyData) {
    commit('SET_SEARCH_COMPANY_DATA', searchCompanyData)
  },
  async listBuildingsByUsername ({ commit, getters, dispatch }, { username }) {
    const { buildingList } = await buildingApiService.listBuildingsByUsername(username)
    dispatch('setSearchLiveTourData', buildingList)
  }
}

export const mutations = {
  SET_SEARCH_DATA (state, markers = []) {
    state.searchData = markers
  },
  SET_SEARCH_INIT (state, bool = false) {
    state.isSearchInit = bool
  },
  SET_SEARCH_KEYWORDS (state, searchKeywords = '') {
    state.searchKeywords = searchKeywords
  },
  SET_SEARCH_CATEGORY (state, searchCategory = '') {
    state.searchCategory = searchCategory
  },
  SET_SELECTED_CATEGORY (state, category = CATEGORY_OPTIONS[0]) {
    state.selectedCategory = category
  },
  SET_SEARCH_LIVETOUR_DATA (state, searchLiveTourData = []) {
    state.searchLiveTourData = searchLiveTourData
  },
  SET_SEARCH_COMPANY_DATA (state, searchCompanyData = []) {
    state.searchCompanyData = searchCompanyData
  }
}

export default {
  state,
  getters,
  actions,
  mutations
}
